/**
 * Lightweight URL <-> section sync without adding react-router-dom.
 *
 * Each top-level "section" maps to a canonical URL.  We honour direct URL
 * navigation, the back/forward buttons, and several aliases (so
 * /register, /onboarding, /admin/clients/new all reach the onboarding page).
 *
 * Sub-pages (per-project drill-down, per-inspection drill-down) are handled
 * by App.jsx state on top of the section.
 */
import { useCallback, useEffect, useState } from 'react';

const SECTION_TO_URL = {
  dashboard:          '/dashboard',
  projects:           '/projects',
  reports:            '/reports',
  alerts:             '/alerts',
  analytics:          '/analytics',
  'pilot-upload':     '/uploads/pilot',
  'annotator-upload': '/uploads/annotator',
  users:              '/users',
  'admin-clients':    '/admin/clients',
};

// Multiple URLs can resolve to the same section.  Keys are matched case-insensitively
// against window.location.pathname after stripping a trailing slash.
const URL_TO_SECTION = [
  [/^\/?$/i,                                       'dashboard'],
  [/^\/dashboard\/?$/i,                            'dashboard'],
  [/^\/projects(\/.*)?$/i,                         'projects'],
  [/^\/reports\/?$/i,                              'reports'],
  [/^\/alerts\/?$/i,                               'alerts'],
  [/^\/analytics\/?$/i,                            'analytics'],
  [/^\/uploads\/pilot\/?$/i,                       'pilot-upload'],
  [/^\/uploads\/annotator\/?$/i,                   'annotator-upload'],
  [/^\/users\/?$/i,                                'users'],
  // Admin / onboarding aliases — all reach the Clients management page,
  // which has an "Onboard Client" button to switch into onboarding mode.
  [/^\/admin\/clients(\/.*)?$/i,                   'admin-clients'],
  [/^\/clients\/?$/i,                              'admin-clients'],
  [/^\/registration\/?$/i,                         'admin-clients'],
  [/^\/register\/?$/i,                             'admin-clients'],
  [/^\/onboard(ing)?\/?$/i,                        'admin-clients'],
];

function urlToSection(pathname) {
  for (const [rx, section] of URL_TO_SECTION) {
    if (rx.test(pathname)) return section;
  }
  return null;
}

export function useUrlRouter(initialSection = 'dashboard') {
  const [section, setSectionState] = useState(() => {
    if (typeof window === 'undefined') return initialSection;
    return urlToSection(window.location.pathname) || initialSection;
  });

  // Listen for back/forward
  useEffect(() => {
    const onPop = () => {
      const next = urlToSection(window.location.pathname) || initialSection;
      setSectionState(next);
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, [initialSection]);

  // Imperative navigate: update state + push URL (idempotent if already there)
  const navigate = useCallback((nextSection) => {
    setSectionState(nextSection);
    const url = SECTION_TO_URL[nextSection];
    if (url && window.location.pathname !== url) {
      window.history.pushState({ section: nextSection }, '', url);
    }
  }, []);

  return [section, navigate];
}
